/**
 * LOT 221 — Theme management (light / dark / system).
 *
 * Stocké dans localStorage uniquement (pas de DB) car :
 *  - Préférence visuelle locale au device
 *  - Pas de besoin de sync cross-device (chaque device a son propre OS)
 *  - Application immédiate sans round-trip serveur
 *
 * Le script inline dans `app/layout.tsx` applique la classe `.dark` sur
 * <html> AVANT le rendu React → pas de flash de mauvais thème (FOUC).
 */

export type ThemePreference = "light" | "dark" | "system";

export const THEME_STORAGE_KEY = "gestic-theme";

/** Lit la préférence stockée (ou "system" par défaut). */
export function getThemePreference(): ThemePreference {
  if (typeof window === "undefined") return "system";
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") return stored;
  return "system";
}

/** Sauvegarde la préférence + applique immédiatement. */
export function setThemePreference(pref: ThemePreference): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(THEME_STORAGE_KEY, pref);
  applyTheme(pref);
}

/** Applique la classe `.dark` sur <html> selon la préférence. */
export function applyTheme(pref: ThemePreference): void {
  if (typeof window === "undefined") return;
  const root = document.documentElement;
  let isDark: boolean;
  if (pref === "dark") isDark = true;
  else if (pref === "light") isDark = false;
  else isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  if (isDark) root.classList.add("dark");
  else root.classList.remove("dark");
  syncFaviconTheme(isDark);
}

/**
 * Aligne le favicon (onglet navigateur) sur le thème effectif : les
 * <link rel="icon" data-favicon="16|32|48"> du layout pointent vers
 * /favicon-{size}.png (light) ou /favicon-{size}-dark.png.
 */
function syncFaviconTheme(isDark: boolean): void {
  const suffix = isDark ? "-dark" : "";
  document.querySelectorAll<HTMLLinkElement>("link[data-favicon]").forEach((l) => {
    const href = `/favicon-${l.dataset.favicon}${suffix}.png`;
    if (l.getAttribute("href") !== href) l.setAttribute("href", href);
  });
}

/** Renvoie le mode effectif courant (light ou dark) selon la préférence. */
export function getEffectiveTheme(pref: ThemePreference): "light" | "dark" {
  if (pref === "dark") return "dark";
  if (pref === "light") return "light";
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * Script inline à injecter dans <head> via `dangerouslySetInnerHTML`.
 * S'exécute SYNCHRONIQUEMENT avant le 1er paint → pas de flash.
 * Ne dépend d'aucun module → string self-contained.
 */
export const themeBootScript = `
(function(){
  try {
    var k = '${THEME_STORAGE_KEY}';
    var p = localStorage.getItem(k);
    var dark = p === 'dark' || ((!p || p === 'system') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    if (dark) document.documentElement.classList.add('dark');
    // Favicons assortis au thème, créés ICI (pas en JSX) : des <link>
    // rendus par React puis mutés avant l'hydratation seraient dupliqués
    // par React (hoisting des liens par href). applyTheme() les retrouve
    // ensuite via [data-favicon] à chaque bascule de thème.
    var sizes = ['16', '32', '48'];
    var suf = dark ? '-dark' : '';
    for (var i = 0; i < sizes.length; i++) {
      var l = document.createElement('link');
      l.rel = 'icon';
      l.type = 'image/png';
      l.setAttribute('sizes', sizes[i] + 'x' + sizes[i]);
      l.setAttribute('data-favicon', sizes[i]);
      l.href = '/favicon-' + sizes[i] + suf + '.png';
      document.head.appendChild(l);
    }
  } catch(e) { /* SSR or localStorage blocked */ }
})();
`;
